import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Alert, Snackbar } from '@mui/material';
import type { AlertColor } from '@mui/material';

import {
  SystemAlertContext,
  type SystemAlertApi,
  type SystemAlertOptions,
} from './systemAlert.context';

type AlertState = {
  open: boolean;
  message: string;
  severity: AlertColor;
  durationMs: number;
};

/**
 * SystemAlertProvider
 *
 * Global, lightweight alert system powered by MUI Snackbar + Alert.
 * Use `useSystemAlert()` to show a toast-style message from any page.
 *
 * Design goals:
 * - Single place to control styling/positioning/duration
 * - No repeated Snackbar logic in each page
 * - Simple API: alert.success(), alert.error(), ...
 */
export function SystemAlertProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AlertState>({
    open: false,
    message: '',
    severity: 'info',
    durationMs: 3000,
  });

  // Track the latest "close" reason to avoid duplicate state churn.
  const lastCloseReasonRef = useRef<string | null>(null);

  const show = useCallback((message: string, options?: SystemAlertOptions) => {
    setState({
      open: true,
      message,
      severity: options?.severity ?? 'info',
      durationMs: options?.durationMs ?? 3000,
    });
  }, []);

  const api = useMemo<SystemAlertApi>(() => {
    return {
      show,
      success: (message, options) => show(message, { ...options, severity: 'success' }),
      info: (message, options) => show(message, { ...options, severity: 'info' }),
      warning: (message, options) => show(message, { ...options, severity: 'warning' }),
      error: (message, options) => show(message, { ...options, severity: 'error' }),
    };
  }, [show]);

  const handleClose = (_: unknown, reason?: string) => {
    // Ignore clickaway to prevent accidental dismiss while interacting.
    if (reason === 'clickaway') return;

    // Avoid repeated close state updates if MUI fires multiple close events.
    if (lastCloseReasonRef.current === reason && !state.open) return;
    lastCloseReasonRef.current = reason ?? null;

    setState((prev) => ({ ...prev, open: false }));
  };

  return (
    <SystemAlertContext.Provider value={api}>
      {children}

      <Snackbar
        open={state.open}
        autoHideDuration={state.durationMs}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => handleClose(null)}
          severity={state.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {state.message}
        </Alert>
      </Snackbar>
    </SystemAlertContext.Provider>
  );
}
